// Thin axios client over /api/v1/projects/*. Every call goes through the
// shared `api` instance so the bearer-token interceptor + 401 redirect keep
// working without ceremony. Move/create/delete all return raw API payloads —
// the store re-fetches the tree afterwards rather than patching in place,
// because /move can re-ordinal a dozen siblings in one swoop and re-fetching
// is cheaper than trying to mirror that math on the client.

import { api } from './apiClient';
import type { Project } from '../types/project';
import type { Guide } from '../types/guide';

export const projectApi = {
  async fetchTree(): Promise<Project[]> {
    const r = await api.get('/api/v1/projects/tree');
    return r.data.projects as Project[];
  },

  async create(name: string, parent_id: string | null): Promise<Project> {
    const r = await api.post('/api/v1/projects', { name, parent_id });
    return r.data.project as Project;
  },

  async rename(id: string, name: string): Promise<Project> {
    const r = await api.patch(`/api/v1/projects/${id}`, { name });
    return r.data.project as Project;
  },

  /** BP 16 — folder Settings tab: patch name / description / visibility. */
  async update(id: string, patch: { name?: string; description?: string; visibility?: 'private' | 'org' }): Promise<Project> {
    const r = await api.patch(`/api/v1/projects/${id}`, patch);
    return r.data.project as Project;
  },

  /** Drag-drop endpoint — moves under `parent_id` at `ordinal`. */
  async move(id: string, parent_id: string | null, ordinal: number): Promise<void> {
    await api.post(`/api/v1/projects/${id}/move`, { parent_id, ordinal });
  },

  /** Recursive counts of what a folder holds — read before offering to delete it. */
  async contents(id: string): Promise<{ id: string; name: string; subfolders: number; guides: number; direct_guides: number }> {
    const r = await api.get(`/api/v1/projects/${id}/contents`);
    return r.data;
  },

  /**
   * Archive a folder AND its subfolders.
   * `unfile` (default) detaches every worksnip in the subtree to Inbox;
   * `cascade` archives them along with the folders.
   */
  async remove(id: string, contents: 'unfile' | 'cascade' = 'unfile'): Promise<void> {
    await api.delete(`/api/v1/projects/${id}`, { params: { contents } });
  },
};

// — Guide-side helpers used by the tree.
// The tree needs the full guide list (so leaves can render under folders) and
// a PATCH wrapper to re-park a guide on drop. Both live here rather than in a
// dedicated guideApi module because the tree is currently the only caller.
export const guideTreeApi = {
  async list(): Promise<Guide[]> {
    const r = await api.get('/api/v1/guides');
    return (r.data.guides || []) as Guide[];
  },

  /** Reassign a guide to a project (or null for "no project"). */
  async setProject(id: string, project_id: string | null): Promise<Guide> {
    const r = await api.patch(`/api/v1/guides/${id}`, { project_id });
    return r.data.guide as Guide;
  },

  /**
   * BP 54 — drag-reorder a guide within (or into) a folder. `orderedIds` is the
   * destination folder's FULL guide-id list in the desired final order,
   * INCLUDING `id` at its target slot; the server stamps ordinal = index.
   * `project_id` is the destination (null = Inbox / unfiled).
   */
  async move(id: string, project_id: string | null, orderedIds: string[]): Promise<void> {
    await api.post(`/api/v1/guides/${id}/move`, { project_id, ordered_ids: orderedIds });
  },

  /** Rename a guide. */
  async rename(id: string, title: string): Promise<Guide> {
    const r = await api.patch(`/api/v1/guides/${id}`, { title });
    return r.data.guide as Guide;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/api/v1/guides/${id}`);
  },

  /** BP 16/37 — bulk file operations (folder Worksnips tab). Each id is
   *  authorized server-side; non-editable snips are reported in `skipped`. */
  async bulk(
    ids: string[],
    op: 'move' | 'status' | 'released' | 'kind' | 'add_tag' | 'assignees',
    value: unknown,
  ): Promise<{ updated: number; skipped: Array<{ id: string; error: number | string }> }> {
    const r = await api.post('/api/v1/guides/bulk', { ids, op, value });
    return r.data;
  },
};
